import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, MapPin, Mic, Camera } from 'lucide-react';
import Layout from '../components/common/Layout';
import SharingInfoDisplay from '../components/common/SharingInfoDisplay';
import { getMemory } from '../services/chaptersAPI';
import { API_CONFIG } from '../config/api';

/**
 * MemoryDetailView - View a single chapter memory
 */
function MemoryDetailView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [memory, setMemory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMemory = async () => {
      try {
        setLoading(true);
        const data = await getMemory(id);
        setMemory(data);
      } catch (err) {
        console.error('Error fetching memory:', err);
        setError('Memory not found');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchMemory();
    }
  }, [id]);

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-fb-cream">
          <div className="max-w-4xl mx-auto px-4 py-8">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-fb-olive mx-auto mb-4"></div>
                <p className="font-serif text-gray-600 text-lg">Loading memory...</p>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !memory) {
    return (
      <Layout>
        <div className="min-h-screen bg-fb-cream">
          <div className="max-w-4xl mx-auto px-4 py-8">
            <div className="bg-white rounded-xl border border-red-200 p-8 text-center">
              <span className="text-5xl mb-4 block">😔</span>
              <h3 className="font-serif font-bold text-xl text-gray-800 mb-2">
                Memory not found
              </h3>
              <p className="font-serif text-gray-500 text-base mb-4">
                We couldn't find this memory.
              </p>
              <Link
                to="/my-life"
                className="text-fb-olive hover:underline font-semibold"
              >
                Back to My Life
              </Link>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const {
    title,
    year,
    text,
    voice_file,
    voice_duration,
    photos,
    chapter,
    created_at,
  } = memory;

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Format duration
  const formatDuration = (seconds) => {
    if (!seconds) return null;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get voice URL
  const voiceUrl = voice_file?.file_url
    ? `${API_CONFIG.BASE_URL}${voice_file.file_url}`
    : null;

  // Get photo URLs
  const photoUrls = photos?.map(p =>
    p.file_url ? `${API_CONFIG.BASE_URL}${p.file_url}` : null
  ).filter(Boolean) || [];

  return (
    <Layout>
      <div className="min-h-screen bg-fb-cream">
        {/* Header */}
        <div className="bg-fb-olive text-white">
          <div className="max-w-4xl mx-auto px-4 py-6">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back</span>
            </button>

            <div className="flex items-start justify-between">
              <div>
                <p className="text-white/70 text-sm mb-1">Memory</p>
                <h1 className="font-serif text-2xl font-bold">
                  {title || 'Untitled Memory'}
                </h1>
                {chapter && (
                  <p className="text-white/80 text-sm mt-1">
                    From Chapter {chapter.number}: "{chapter.title}"
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {/* Meta info */}
            <div className="px-6 py-4 border-b border-gray-100 flex flex-wrap gap-4 text-sm text-gray-600">
              {year && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>{year}</span>
                </div>
              )}
              {created_at && (
                <div className="flex items-center gap-2">
                  <span className="text-gray-400">Added:</span>
                  <span>{formatDate(created_at)}</span>
                </div>
              )}
            </div>

            {/* Sharing Info - memories are always owned by the current user */}
            <SharingInfoDisplay
              sharingScope={memory.sharing_scope}
              originFamilyId={memory.origin_family}
              sharedWithFamilies={memory.shared_with_families || []}
              isOwner={true}
              className="px-6 py-2 border-b border-gray-100"
            />

            {/* Voice recording */}
            {voiceUrl && (
              <div className="px-6 py-4 border-b border-gray-100 bg-green-50">
                <div className="flex items-center gap-2 mb-3">
                  <Mic className="w-5 h-5 text-fb-olive" />
                  <span className="font-semibold text-fb-olive">Voice Recording</span>
                  {voice_duration && (
                    <span className="text-sm text-gray-500">
                      ({formatDuration(voice_duration)})
                    </span>
                  )}
                </div>
                <audio controls className="w-full">
                  <source src={voiceUrl} />
                  Your browser does not support audio playback.
                </audio>
              </div>
            )}

            {/* Text content */}
            {text && (
              <div className="px-6 py-6">
                <p className="font-serif text-lg text-gray-800 whitespace-pre-wrap leading-relaxed">
                  {text}
                </p>
              </div>
            )}

            {/* Photos */}
            {photoUrls.length > 0 && (
              <div className="px-6 py-4 border-t border-gray-100">
                <div className="flex items-center gap-2 mb-4">
                  <Camera className="w-5 h-5 text-gray-600" />
                  <span className="font-semibold text-gray-700">
                    Photos ({photoUrls.length})
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {photoUrls.map((url, idx) => (
                    <img
                      key={idx}
                      src={url}
                      alt={`Memory photo ${idx + 1}`}
                      className="w-full h-48 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => window.open(url, '_blank')}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Empty state */}
            {!text && !voiceUrl && photoUrls.length === 0 && (
              <div className="px-6 py-12 text-center text-gray-500">
                <p className="font-serif text-lg">No content added to this memory yet.</p>
              </div>
            )}
          </div>

          {/* Back to chapter link */}
          {chapter && (
            <div className="mt-6 text-center">
              <Link
                to={`/my-life/chapters/${chapter.id}`}
                className="text-fb-olive hover:underline font-semibold"
              >
                View all memories in "{chapter.title}"
              </Link>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default MemoryDetailView;
